"use client";

import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import type { ProductFocus } from "@/constants/onboarding";
import { DaysOfWeek } from "@/enums/settings";
import { useOrg } from "@/hooks/useOrg";
import { ScheduleDay } from "@/types/profile";
import { schedulesMatch } from "@/utils/common/holidays";
import {
  ALL_DAYS,
  DEFAULT_END,
  DEFAULT_START,
  WEEKDAYS,
  WEEKEND_DAYS,
  createDefaultDayHours,
  createSlot,
  deriveStateFromSchedule,
  getHoursSummary,
  validateSchedule,
  type DayHoursMap,
  type DaysActiveOption,
  type TimesActiveOption,
} from "@/utils/onboarding/businessHoursUtils";
import { CustomSchedulePanel } from "./CustomSchedulePanel";
import { OnboardingStepHeader } from "./OnboardingStepHeader";
import { ScheduleCard } from "./ScheduleCard";
import { StepFooter } from "./StepFooter";

interface BusinessHoursStepProps {
  readonly onBack?: () => void;
  readonly onNext: () => void;
  readonly productFocus?: ProductFocus[];
}

export function BusinessHoursStep({ onBack, onNext, productFocus }: BusinessHoursStepProps) {
  const { t } = useTranslation("onboarding");
  const { agentProfile, saveAgentProfile } = useOrg();

  const [isSaving, setIsSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  /** Snapshot of the schedule at load time – used to detect changes. */
  const initialScheduleRef = useRef<string | null>(null);
  /** Whether the backend had a pre-existing schedule when the component mounted. */
  const hadExistingScheduleRef = useRef(false);

  // Core state (inbound/business hours)
  const [daysActive, setDaysActive] = useState<DaysActiveOption>("weekdays");
  const [timesActive, setTimesActive] = useState<TimesActiveOption>("businessHours");
  const [selectedDays, setSelectedDays] = useState<DaysOfWeek[]>(WEEKDAYS);
  const [dayHours, setDayHours] = useState<DayHoursMap>(createDefaultDayHours);
  const [sameHoursAllWeek, setSameHoursAllWeek] = useState(true);
  const [sameHoursAllWeekend, setSameHoursAllWeekend] = useState(true);

  // Copy mode state – when set, the source day shows "Done" and every other day shows "Paste"
  const [copySourceDay, setCopySourceDay] = useState<DaysOfWeek | null>(null);

  // Outbound schedule differentiation
  const hasBothInboundAndOutbound =
    productFocus?.includes("inbound") && productFocus?.includes("outbound");
  const [useSameAsInbound, setUseSameAsInbound] = useState(true);

  // Outbound-specific schedule state (only used when useSameAsInbound is false)
  const [outboundDaysActive, setOutboundDaysActive] = useState<DaysActiveOption>("weekdays");
  const [outboundTimesActive, setOutboundTimesActive] =
    useState<TimesActiveOption>("businessHours");
  const [outboundSelectedDays, setOutboundSelectedDays] = useState<DaysOfWeek[]>(WEEKDAYS);
  const [outboundDayHours, setOutboundDayHours] = useState<DayHoursMap>(createDefaultDayHours);

  // Translated dropdown options
  const daysActiveOptions = useMemo(
    () => [
      { value: "weekdays", label: t("businessHours.weekdays") },
      { value: "weekends", label: t("businessHours.weekends") },
      { value: "allDays", label: t("businessHours.allDays") },
      { value: "custom", label: t("businessHours.custom") },
    ],
    [t]
  );

  const timesActiveOptions = useMemo(
    () => [
      { value: "businessHours", label: t("businessHours.businessHoursLabel") },
      { value: "allHours", label: t("businessHours.allHours") },
      { value: "custom", label: t("businessHours.custom") },
    ],
    [t]
  );

  // Translated time options (12:00 am ... 11:30 pm)
  const timeOptions = useMemo(() => {
    const am = t("businessHours.time.am");
    const pm = t("businessHours.time.pm");
    return Array.from({ length: 48 }, (_, i) => {
      const hour = Math.floor(i / 2);
      const minute = i % 2 === 0 ? "00" : "30";
      const period = hour < 12 ? am : pm;
      let displayHour = hour;
      if (hour === 0) {
        displayHour = 12;
      } else if (hour > 12) {
        displayHour = hour - 12;
      }
      const value = `${hour.toString().padStart(2, "0")}:${minute}`;
      const label = `${displayHour}:${minute} ${period}`;
      return { value, label };
    });
  }, [t]);

  // ------------------------------------------------------------------
  // Initialize from existing profile
  // ------------------------------------------------------------------
  useEffect(() => {
    if (initialized) return;
    const existingSchedule = agentProfile?.operationSchedule?.businessSchedule;
    const existingOutbound = agentProfile?.operationSchedule?.outboundSchedule;

    if (!existingSchedule || existingSchedule.length === 0) {
      setInitialized(true);
      return;
    }

    hadExistingScheduleRef.current = true;
    const derived = deriveStateFromSchedule(existingSchedule);
    setDaysActive(derived.daysActive);
    setTimesActive(derived.timesActive);
    setSelectedDays(derived.selectedDays);
    setDayHours(derived.dayHours);
    setSameHoursAllWeek(derived.sameHoursAllWeek);
    setSameHoursAllWeekend(derived.sameHoursAllWeekend);

    if (existingOutbound && existingOutbound.length > 0) {
      const matchesInbound = schedulesMatch(existingSchedule, existingOutbound);
      setUseSameAsInbound(matchesInbound);

      if (!matchesInbound) {
        const outboundDerived = deriveStateFromSchedule(existingOutbound);
        setOutboundDaysActive(outboundDerived.daysActive);
        setOutboundTimesActive(outboundDerived.timesActive);
        setOutboundSelectedDays(outboundDerived.selectedDays);
        setOutboundDayHours(outboundDerived.dayHours);
      }
    }

    setInitialized(true);
  }, [
    agentProfile?.operationSchedule?.businessSchedule,
    agentProfile?.operationSchedule?.outboundSchedule,
    initialized,
  ]);

  // Clear validation error when user changes anything
  useEffect(() => {
    if (validationError) setValidationError(null);
  }, [
    selectedDays,
    dayHours,
    daysActive,
    timesActive,
    outboundSelectedDays,
    outboundDayHours,
    outboundDaysActive,
    outboundTimesActive,
    useSameAsInbound,
  ]);

  // ------------------------------------------------------------------
  // Derived booleans
  // ------------------------------------------------------------------
  const showCustomDays = daysActive === "custom";
  const showCustomTimes = timesActive === "custom";
  const showCustomOptions = showCustomDays || showCustomTimes;

  const hasAllWeekdays = WEEKDAYS.every((d) => selectedDays.includes(d));
  const hasAllWeekends = WEEKEND_DAYS.every((d) => selectedDays.includes(d));

  const selectedWeekdays = useMemo(
    () => WEEKDAYS.filter((d) => selectedDays.includes(d)),
    [selectedDays]
  );

  // ------------------------------------------------------------------
  // Day-hours helpers
  // ------------------------------------------------------------------

  const updateDaySlot = useCallback(
    (day: DaysOfWeek, slotIndex: number, field: "start" | "end", value: string) => {
      setDayHours((prev) => ({
        ...prev,
        [day]: prev[day].map((slot, i) => (i === slotIndex ? { ...slot, [field]: value } : slot)),
      }));
    },
    []
  );

  const updateGroupSlot = useCallback(
    (group: DaysOfWeek[], slotIndex: number, field: "start" | "end", value: string) => {
      setDayHours((prev) => {
        const next = { ...prev };
        for (const day of group) {
          next[day] = next[day].map((slot, i) =>
            i === slotIndex ? { ...slot, [field]: value } : slot
          );
        }
        return next;
      });
    },
    []
  );

  const addTimeSlot = useCallback((day: DaysOfWeek) => {
    setDayHours((prev) => {
      const slots = prev[day];
      const lastEnd = slots.at(-1)?.end || "17:00";
      return {
        ...prev,
        [day]: [...slots, createSlot(lastEnd, "")],
      };
    });
  }, []);

  const removeTimeSlot = useCallback((day: DaysOfWeek, slotIndex: number) => {
    setDayHours((prev) => {
      const slots = prev[day];
      if (slots.length <= 1) {
        return { ...prev, [day]: [createSlot(DEFAULT_START, DEFAULT_END)] };
      }
      return {
        ...prev,
        [day]: slots.filter((_, i) => i !== slotIndex),
      };
    });
  }, []);

  const addGroupSlot = useCallback((group: DaysOfWeek[], referenceDay: DaysOfWeek) => {
    setDayHours((prev) => {
      const next = { ...prev };
      const refDay = group.includes(referenceDay) ? referenceDay : group[0];
      const refSlots = next[refDay];
      const lastEnd = refSlots.at(-1)?.end || "17:00";
      const newSlot = createSlot(lastEnd, "");
      for (const day of group) {
        next[day] = [...next[day], createSlot(newSlot.start, newSlot.end)];
      }
      return next;
    });
  }, []);

  const removeGroupSlot = useCallback((group: DaysOfWeek[], slotIndex: number) => {
    setDayHours((prev) => {
      const next = { ...prev };
      for (const day of group) {
        const slots = next[day];
        if (slots.length <= 1) {
          next[day] = [createSlot(DEFAULT_START, DEFAULT_END)];
        } else {
          next[day] = slots.filter((_, i) => i !== slotIndex);
        }
      }
      return next;
    });
  }, []);

  // ------------------------------------------------------------------
  // Copy mode
  // ------------------------------------------------------------------

  const enterCopyMode = useCallback((sourceDay: DaysOfWeek) => {
    setCopySourceDay(sourceDay);
  }, []);

  const exitCopyMode = useCallback(() => {
    setCopySourceDay(null);
  }, []);

  const pasteToDay = useCallback(
    (targetDay: DaysOfWeek) => {
      if (!copySourceDay) return;
      const sourceSlots = dayHours[copySourceDay];
      setDayHours((prev) => ({
        ...prev,
        [targetDay]: sourceSlots.map((s) => createSlot(s.start, s.end)),
      }));
    },
    [copySourceDay, dayHours]
  );

  // ------------------------------------------------------------------
  // Toggle handlers
  // ------------------------------------------------------------------

  const handleSameHoursAllWeekChange = useCallback(
    (checked: boolean) => {
      setSameHoursAllWeek(checked);
      if (checked && selectedWeekdays.length > 0) {
        const source = dayHours[selectedWeekdays[0]];
        setDayHours((prev) => {
          const next = { ...prev };
          for (const day of WEEKDAYS) {
            next[day] = source.map((s) => createSlot(s.start, s.end));
          }
          return next;
        });
      }
    },
    [selectedWeekdays, dayHours]
  );

  const selectedWeekendDays = useMemo(
    () => WEEKEND_DAYS.filter((d) => selectedDays.includes(d)),
    [selectedDays]
  );

  const handleSameHoursAllWeekendChange = useCallback(
    (checked: boolean) => {
      setSameHoursAllWeekend(checked);
      if (checked && selectedWeekendDays.length > 0) {
        const source = dayHours[selectedWeekendDays[0]];
        setDayHours((prev) => {
          const next = { ...prev };
          for (const day of WEEKEND_DAYS) {
            next[day] = source.map((s) => createSlot(s.start, s.end));
          }
          return next;
        });
      }
    },
    [selectedWeekendDays, dayHours]
  );

  // ------------------------------------------------------------------
  // Days-active / times-active dropdown handlers
  // ------------------------------------------------------------------

  const handleDaysActiveChange = useCallback((value: string) => {
    const option = value as DaysActiveOption;
    setDaysActive(option);

    switch (option) {
      case "weekdays":
        setSelectedDays(WEEKDAYS);
        break;
      case "weekends":
        setSelectedDays(WEEKEND_DAYS);
        break;
      case "allDays":
        setSelectedDays(ALL_DAYS);
        break;
      case "custom":
        break;
    }
  }, []);

  const handleTimesActiveChange = useCallback(
    (value: string) => {
      const option = value as TimesActiveOption;
      setTimesActive(option);

      let newStart = dayHours[WEEKDAYS[0]][0].start;
      let newEnd = dayHours[WEEKDAYS[0]][0].end;

      if (option === "businessHours") {
        newStart = "09:00";
        newEnd = "17:00";
      } else if (option === "allHours") {
        newStart = "00:00";
        newEnd = "23:59";
      }

      if (option !== "custom") {
        setDayHours((prev) => {
          const next = { ...prev };
          for (const day of ALL_DAYS) {
            next[day] = [createSlot(newStart, newEnd)];
          }
          return next;
        });
        setSameHoursAllWeek(true);
        setSameHoursAllWeekend(true);
      }
    },
    [dayHours]
  );

  // ------------------------------------------------------------------
  // Day selection handlers
  // ------------------------------------------------------------------

  const toggleDay = useCallback((day: DaysOfWeek) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
    setDaysActive("custom");
  }, []);

  const selectWeekdays = useCallback(() => {
    setSelectedDays((prev) => {
      const allSelected = WEEKDAYS.every((d) => prev.includes(d));
      if (allSelected) {
        return prev.filter((d) => !WEEKDAYS.includes(d));
      }
      return [...new Set([...prev, ...WEEKDAYS])];
    });
    setDaysActive("custom");
  }, []);

  const selectWeekends = useCallback(() => {
    setSelectedDays((prev) => {
      const allSelected = WEEKEND_DAYS.every((d) => prev.includes(d));
      if (allSelected) {
        return prev.filter((d) => !WEEKEND_DAYS.includes(d));
      }
      return [...new Set([...prev, ...WEEKEND_DAYS])];
    });
    setDaysActive("custom");
  }, []);

  // ------------------------------------------------------------------
  // Outbound schedule callbacks
  // ------------------------------------------------------------------

  const handleOutboundDaysActiveChange = useCallback((value: string) => {
    const option = value as DaysActiveOption;
    setOutboundDaysActive(option);

    switch (option) {
      case "weekdays":
        setOutboundSelectedDays(WEEKDAYS);
        break;
      case "weekends":
        setOutboundSelectedDays(WEEKEND_DAYS);
        break;
      case "allDays":
        setOutboundSelectedDays(ALL_DAYS);
        break;
      case "custom":
        break;
    }
  }, []);

  const handleOutboundTimesActiveChange = useCallback(
    (value: string) => {
      const option = value as TimesActiveOption;
      setOutboundTimesActive(option);

      let newStart = outboundDayHours[WEEKDAYS[0]][0].start;
      let newEnd = outboundDayHours[WEEKDAYS[0]][0].end;

      if (option === "businessHours") {
        newStart = "09:00";
        newEnd = "17:00";
      } else if (option === "allHours") {
        newStart = "00:00";
        newEnd = "23:59";
      }

      if (option !== "custom") {
        setOutboundDayHours((prev) => {
          const next = { ...prev };
          for (const day of ALL_DAYS) {
            next[day] = [createSlot(newStart, newEnd)];
          }
          return next;
        });
      }
    },
    [outboundDayHours]
  );

  const updateOutboundGroupSlot = useCallback(
    (group: DaysOfWeek[], slotIndex: number, field: "start" | "end", value: string) => {
      setOutboundDayHours((prev) => {
        const next = { ...prev };
        for (const day of group) {
          next[day] = next[day].map((slot, i) =>
            i === slotIndex ? { ...slot, [field]: value } : slot
          );
        }
        return next;
      });
    },
    []
  );

  const toggleOutboundDay = useCallback((day: DaysOfWeek) => {
    setOutboundSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      }
      return [...prev, day];
    });
    setOutboundDaysActive("custom");
  }, []);

  const selectOutboundWeekdays = useCallback(() => {
    setOutboundSelectedDays((prev) => {
      const allSelected = WEEKDAYS.every((d) => prev.includes(d));
      if (allSelected) {
        return prev.filter((d) => !WEEKDAYS.includes(d));
      }
      return [...new Set([...prev, ...WEEKDAYS])];
    });
    setOutboundDaysActive("custom");
  }, []);

  const selectOutboundWeekends = useCallback(() => {
    setOutboundSelectedDays((prev) => {
      const allSelected = WEEKEND_DAYS.every((d) => prev.includes(d));
      if (allSelected) {
        return prev.filter((d) => !WEEKEND_DAYS.includes(d));
      }
      return [...new Set([...prev, ...WEEKEND_DAYS])];
    });
    setOutboundDaysActive("custom");
  }, []);

  // Outbound derived booleans
  const outboundShowCustomDays = outboundDaysActive === "custom";
  const outboundShowCustomTimes = outboundTimesActive === "custom";
  const outboundShowCustomOptions = outboundShowCustomDays || outboundShowCustomTimes;

  const outboundHasAllWeekdays = WEEKDAYS.every((d) => outboundSelectedDays.includes(d));
  const outboundHasAllWeekends = WEEKEND_DAYS.every((d) => outboundSelectedDays.includes(d));

  // ------------------------------------------------------------------
  // Build schedule & summary
  // ------------------------------------------------------------------

  const buildSchedule = useCallback((): ScheduleDay[] => {
    return ALL_DAYS.map((day) => {
      if (!selectedDays.includes(day)) {
        return { day, hours: [] };
      }
      const validSlots = dayHours[day]
        .filter((s) => s.start)
        .map(({ start, end }) => ({ start, end }));
      return { day, hours: validSlots };
    });
  }, [selectedDays, dayHours]);

  const buildOutboundSchedule = useCallback((): ScheduleDay[] => {
    return ALL_DAYS.map((day) => {
      if (!outboundSelectedDays.includes(day)) {
        return { day, hours: [] };
      }
      const validSlots = outboundDayHours[day]
        .filter((s) => s.start)
        .map(({ start, end }) => ({ start, end }));
      return { day, hours: validSlots };
    });
  }, [outboundSelectedDays, outboundDayHours]);

  const hoursSummary = useMemo(() => {
    return getHoursSummary(buildSchedule(), t);
  }, [buildSchedule, t]);

  const outboundHoursSummary = useMemo(() => {
    return getHoursSummary(buildOutboundSchedule(), t);
  }, [buildOutboundSchedule, t]);

  // ------------------------------------------------------------------
  // Save (only when there are actual changes)
  // ------------------------------------------------------------------

  const serializeSchedule = useCallback(() => {
    const builtSchedule = buildSchedule();
    const cleanedSchedule = builtSchedule.filter(
      (s) => s.hours && s.hours.length > 0 && s.hours[0].start
    );
    const builtOutbound = buildOutboundSchedule();
    const cleanedOutbound = builtOutbound.filter(
      (s) => s.hours && s.hours.length > 0 && s.hours[0].start
    );
    return JSON.stringify({
      business: cleanedSchedule,
      outbound: cleanedOutbound,
      useSameAsInbound,
    });
  }, [buildSchedule, buildOutboundSchedule, useSameAsInbound]);

  useEffect(() => {
    if (initialized && hadExistingScheduleRef.current && initialScheduleRef.current === null) {
      initialScheduleRef.current = serializeSchedule();
    }
  }, [initialized, serializeSchedule]);

  const hasChanges = useCallback(() => {
    return serializeSchedule() !== initialScheduleRef.current;
  }, [serializeSchedule]);

  const saveAndNavigate = useCallback(
    async (navigateFn: () => void) => {
      if (!hasChanges()) {
        navigateFn();
        return;
      }

      setIsSaving(true);
      try {
        const builtSchedule = buildSchedule();
        const cleanedSchedule = builtSchedule.filter(
          (s) => s.hours && s.hours.length > 0 && s.hours[0].start
        );

        let cleanedOutbound: ScheduleDay[];
        if (useSameAsInbound || !hasBothInboundAndOutbound) {
          cleanedOutbound = cleanedSchedule;
        } else {
          const builtOutbound = buildOutboundSchedule();
          cleanedOutbound = builtOutbound.filter(
            (s) => s.hours && s.hours.length > 0 && s.hours[0].start
          );
        }

        await saveAgentProfile({
          operationSchedule: {
            timeZone:
              agentProfile?.operationSchedule?.timeZone ||
              Intl.DateTimeFormat().resolvedOptions().timeZone,
            businessSchedule: cleanedSchedule,
            outboundSchedule: cleanedOutbound,
          },
        });

        initialScheduleRef.current = serializeSchedule();
        navigateFn();
      } catch (error) {
        console.error("Failed to save business hours", error);
      } finally {
        setIsSaving(false);
      }
    },
    [
      hasChanges,
      buildSchedule,
      buildOutboundSchedule,
      serializeSchedule,
      saveAgentProfile,
      agentProfile?.operationSchedule?.timeZone,
      useSameAsInbound,
      hasBothInboundAndOutbound,
    ]
  );

  const handleNext = useCallback(() => {
    const error = validateSchedule(selectedDays, dayHours, t);
    if (error) {
      setValidationError(error);
      return;
    }
    if (hasBothInboundAndOutbound && !useSameAsInbound) {
      const outboundError = validateSchedule(outboundSelectedDays, outboundDayHours, t);
      if (outboundError) {
        setValidationError(outboundError);
        return;
      }
    }
    saveAndNavigate(onNext);
  }, [
    selectedDays,
    dayHours,
    saveAndNavigate,
    onNext,
    t,
    hasBothInboundAndOutbound,
    useSameAsInbound,
    outboundSelectedDays,
    outboundDayHours,
  ]);

  const handleBack = useCallback(
    () => onBack && saveAndNavigate(onBack),
    [saveAndNavigate, onBack]
  );

  // ------------------------------------------------------------------
  // JSX
  // ------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-8">
        <div className="max-w-xl mx-auto space-y-6">
          {/* Header */}
          <OnboardingStepHeader
            title={t("businessHours.title")}
            description={t("businessHours.subtitle")}
          />

          {/* Main Card — Inbound / Business Hours */}
          <ScheduleCard
            title={
              hasBothInboundAndOutbound
                ? t("businessHours.inboundCardTitle")
                : t("businessHours.cardTitle")
            }
            description={
              hasBothInboundAndOutbound
                ? t("businessHours.inboundCardDescription")
                : t("businessHours.cardDescription")
            }
            daysActive={daysActive}
            timesActive={timesActive}
            daysActiveOptions={daysActiveOptions}
            timesActiveOptions={timesActiveOptions}
            onDaysActiveChange={handleDaysActiveChange}
            onTimesActiveChange={handleTimesActiveChange}
            hoursSummary={hoursSummary}
            summaryLabel={t("businessHours.currentHours")}
            showCustomOptions={showCustomOptions}
            onEditClick={() => {
              setDaysActive("custom");
              setTimesActive("custom");
            }}
          >
            {showCustomOptions && (
              <CustomSchedulePanel
                showCustomDays={showCustomDays}
                showCustomTimes={showCustomTimes}
                selectedDays={selectedDays}
                dayHours={dayHours}
                timeOptions={timeOptions}
                hasAllWeekdays={hasAllWeekdays}
                hasAllWeekends={hasAllWeekends}
                onSelectWeekdays={selectWeekdays}
                onSelectWeekends={selectWeekends}
                onToggleDay={toggleDay}
                onUpdateGroupSlot={updateGroupSlot}
                onUpdateDaySlot={updateDaySlot}
                onAddTimeSlot={addTimeSlot}
                onRemoveTimeSlot={removeTimeSlot}
                onEnterCopyMode={enterCopyMode}
                onExitCopyMode={exitCopyMode}
                onPasteToDay={pasteToDay}
                copySourceDay={copySourceDay}
                sameHoursAllWeek={sameHoursAllWeek}
                sameHoursAllWeekend={sameHoursAllWeekend}
                onSameHoursAllWeekChange={handleSameHoursAllWeekChange}
                onSameHoursAllWeekendChange={handleSameHoursAllWeekendChange}
                onAddGroupSlot={addGroupSlot}
                onRemoveGroupSlot={removeGroupSlot}
              />
            )}
          </ScheduleCard>

          {/* Outbound Call Hours — only when both inbound and outbound are selected */}
          {hasBothInboundAndOutbound && (
            <div className="rounded-2xl border border-200 bg-panel p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {t("businessHours.outboundCardTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {t("businessHours.outboundCardDescription")}
                </p>
              </div>

              {/* Use same as inbound checkbox */}
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={useSameAsInbound} onCheckedChange={setUseSameAsInbound} />
                <span className="text-sm font-medium text-foreground">
                  {t("businessHours.useSameAsInbound")}
                </span>
              </label>

              {/* Outbound schedule editor (hidden when using same as inbound) */}
              <div
                className={clsx(
                  "grid transition-[grid-template-rows] duration-300 ease-in-out",
                  useSameAsInbound ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
                )}
              >
                <div className="overflow-hidden">
                  <ScheduleCard
                    title=""
                    description=""
                    daysActive={outboundDaysActive}
                    timesActive={outboundTimesActive}
                    daysActiveOptions={daysActiveOptions}
                    timesActiveOptions={timesActiveOptions}
                    onDaysActiveChange={handleOutboundDaysActiveChange}
                    onTimesActiveChange={handleOutboundTimesActiveChange}
                    hoursSummary={outboundHoursSummary}
                    summaryLabel={t("businessHours.outboundCurrentHours")}
                    showCustomOptions={outboundShowCustomOptions}
                    onEditClick={() => {
                      setOutboundDaysActive("custom");
                      setOutboundTimesActive("custom");
                    }}
                    summaryColorClass="bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                  >
                    {outboundShowCustomOptions && (
                      <CustomSchedulePanel
                        showCustomDays={outboundShowCustomDays}
                        showCustomTimes={outboundShowCustomTimes}
                        selectedDays={outboundSelectedDays}
                        dayHours={outboundDayHours}
                        timeOptions={timeOptions}
                        hasAllWeekdays={outboundHasAllWeekdays}
                        hasAllWeekends={outboundHasAllWeekends}
                        onSelectWeekdays={selectOutboundWeekdays}
                        onSelectWeekends={selectOutboundWeekends}
                        onToggleDay={toggleOutboundDay}
                        onUpdateGroupSlot={updateOutboundGroupSlot}
                        onUpdateDaySlot={(day, slotIndex, field, value) => {
                          setOutboundDayHours((prev) => ({
                            ...prev,
                            [day]: prev[day].map((slot, i) =>
                              i === slotIndex ? { ...slot, [field]: value } : slot
                            ),
                          }));
                        }}
                        onAddTimeSlot={() => {}}
                        onRemoveTimeSlot={() => {}}
                        onEnterCopyMode={() => {}}
                        onExitCopyMode={() => {}}
                        onPasteToDay={() => {}}
                        copySourceDay={null}
                      />
                    )}
                  </ScheduleCard>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Buttons */}
      <StepFooter
        onBack={handleBack}
        onSkip={onNext}
        onNext={handleNext}
        isSaving={isSaving}
        validationError={validationError ?? undefined}
      />
    </div>
  );
}
